import type { CatalogEntry, PackageKind } from './catalog.js';
import { catalogForGroup, STANDARD_CATALOG } from './catalog.js';
import {
  getInstallClient,
  type InstallClient,
  type InstallOutcome,
  type Prerequisite,
} from './install-client.js';
import './package-picker.css';

/**
 * CAOS-211 — the empty-workspace package picker.
 *
 * A `<caos-overlay>` centred modal, opened by an empty-workspace CTA, that lists the standard
 * packages for its group (workspace / app), installs a chosen entry through the {@link
 * InstallClient} boundary with progress + error states, and on success invokes the caller's
 * `onInstalled` (the reboot seam) so the host re-runs the boot decision and re-renders. The
 * Engine stays pixel-free; this is pure host UI over the install seam.
 *
 * Presence in the DOM is "open" (matching `<caos-overlay>` semantics): the picker mounts itself
 * into `document.body` (overridable) and unmounts on close, on Escape / scrim click, or after a
 * successful install.
 */

/** What the picker needs to open. */
export interface PackagePickerDeps {
  /** Which CTA group to list — the workspace packages or the app packages. */
  readonly group: PackageKind;
  /** Invoked once, after a successful install, before the picker closes — the reboot seam. */
  readonly onInstalled: () => void | Promise<void>;
  /** The install boundary; defaults to the active {@link getInstallClient}. */
  readonly client?: InstallClient;
  /** The catalog to draw entries from; defaults to the standard bundled catalog. */
  readonly catalog?: readonly CatalogEntry[];
  /** Where to mount the overlay; defaults to `document.body`. */
  readonly mountInto?: HTMLElement;
  /**
   * CAOS-287 — the package keys ALREADY installed in this org.
   *
   * Supplying it switches the picker from first-run onboarding to the post-workspace "Get more
   * apps" surface: an entry whose packages are all present renders as INSTALLED instead of
   * offering a button, and the copy speaks of adding to a workspace that exists rather than
   * setting one up. Absent — unchanged first-run behaviour.
   *
   * The mode is keyed on PRESENCE of the set, not on whether it is empty. An org that has a
   * workspace but no apps yet passes an empty set and is still in the "get more apps" surface;
   * conflating the two would make a real state (nothing installed yet) read as first-run.
   */
  readonly installedPackageKeys?: ReadonlySet<string>;
}

/** A handle on an open picker — for tests and for programmatic close. */
export interface PackagePickerHandle {
  /** The mounted `<caos-overlay>` element. */
  readonly element: HTMLElement;
  /** Unmount the picker. */
  close(): void;
  /** Run the install for one entry (the same path its button drives). Returns the outcome. */
  installEntry(entryId: string): Promise<InstallOutcome>;
}

const COPY: Record<PackageKind, { eyebrow: string; title: string; subtitle: string }> = {
  workspace: {
    eyebrow: 'Set up your workspace',
    title: 'Get your team’s home base ready',
    subtitle: 'Set up the home base your team works from — search, navigation, and the tools admins use to add people and manage access. Do this once, then add the apps you need.',
  },
  app: {
    eyebrow: 'Add an app',
    title: 'Add your first app',
    subtitle: 'Pick an app to add to your workspace. It’s ready to use the moment it finishes installing, for everyone you give access to.',
  },
};

/**
 * CAOS-287 — copy for the in-shell front door, where a workspace already exists.
 *
 * Separate from {@link COPY} rather than a third `PackageKind`, because the kind says WHAT is being
 * installed and this says WHERE the person is standing: the same `app` group is offered on both
 * the first run and from the launcher, and only the words around it differ.
 */
const MORE_APPS_COPY = {
  eyebrow: 'Get more apps',
  title: 'Add apps to your workspace',
  subtitle:
    'Browse the apps available to your workspace and add the ones your team needs. Each one is ready to use the moment it finishes installing, for everyone you give access to.',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * CAOS-401 — how a required package is named to a person.
 *
 * Derived from the catalog rather than from a second hand-kept table of package labels: an entry
 * already knows which packages it ships, so the entry that ships `pkg_core_shell` is the one whose
 * label says "Workspace". A key no entry covers falls back to the key itself, which is ugly and
 * honest — far better than omitting a requirement because nothing had a pretty name for it.
 */
function labelForPackage(key: string, catalog: readonly CatalogEntry[]): string {
  return catalog.find((e) => e.packages.includes(key))?.label ?? key;
}

/**
 * CAOS-401 — the sentence under an entry saying what it needs.
 *
 * Split by whether the workspace already holds the thing, because those are two different pieces
 * of news: one is reassurance, the other is a consequence of pressing the button. A requirement
 * that is already met is still stated — "it needs the workspace, and you have it" is the whole
 * point, and silence there is indistinguishable from needing nothing.
 */
function prerequisiteSentence(
  prerequisites: readonly Prerequisite[],
  catalog: readonly CatalogEntry[],
): string {
  const named = (keys: string[]): string => {
    const labels = [...new Set(keys)].map((k) => labelForPackage(k, catalog));
    if (labels.length <= 1) return labels.join('');
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]!}`;
  };

  const held = named(prerequisites.filter((p) => p.status === 'already-installed').map((p) => p.package));
  const coming = named(prerequisites.filter((p) => p.status === 'will-install').map((p) => p.package));
  const blocked = named(prerequisites.filter((p) => p.status === 'unresolved').map((p) => p.package));

  const parts: string[] = [];
  if (blocked) parts.push(`needs ${blocked}, which is not available`);
  if (coming) parts.push(`installs ${coming} with it`);
  if (held) parts.push(`needs ${held}, which you already have`);
  if (parts.length === 0) return '';
  return `This ${parts.join('; ')}.`;
}

/** Open the package picker. Returns a handle; the picker mounts itself immediately. */
export function openPackagePicker(deps: PackagePickerDeps): PackagePickerHandle {
  const client = deps.client ?? getInstallClient();
  const mountInto = deps.mountInto ?? document.body;
  const entries = catalogForGroup(deps.group, deps.catalog);
  // CAOS-287 — presence of the installed set is the mode switch; see the field's docblock.
  const isMoreApps = deps.installedPackageKeys !== undefined;
  const installedKeys = deps.installedPackageKeys ?? new Set<string>();
  /**
   * An entry counts as installed only when EVERY package it ships is present. A catalog entry can
   * span more than one package (the workspace entry does), and calling it installed on a partial
   * match would hide the action that completes it — leaving the org permanently half-installed with
   * no way to say so from here.
   */
  const isInstalled = (entry: CatalogEntry): boolean =>
    entry.packages.length > 0 && entry.packages.every((pkg) => installedKeys.has(pkg));
  const copy = isMoreApps ? MORE_APPS_COPY : COPY[deps.group];
  /** CAOS-401 — the per-entry line that says what it requires, filled in once the Engine answers. */
  const prerequisiteSlots = new Map<string, HTMLElement>();
  const catalogEntries = deps.catalog ?? STANDARD_CATALOG;

  const overlay = document.createElement('caos-overlay');
  overlay.setAttribute('placement', 'center');
  overlay.setAttribute('label', copy.title);

  const wrap = el('div', 'caos-picker');

  // Head
  const head = el('div', 'caos-picker__head');
  head.append(
    el('p', 'caos-picker__eyebrow', copy.eyebrow),
    el('h2', 'caos-picker__title', copy.title),
    el('p', 'caos-picker__subtitle', copy.subtitle),
  );
  wrap.append(head);

  // Status region (progress bar / error banner live here)
  const status = el('div', 'caos-picker__status');

  // Entry list
  const list = el('div', 'caos-picker__list');
  const installButtons: HTMLElement[] = [];

  let installing = false;

  const setBusy = (busy: boolean, active?: HTMLElement): void => {
    installing = busy;
    for (const b of installButtons) {
      if (busy) b.setAttribute('disabled', '');
      else b.removeAttribute('disabled');
      if (b === active && busy) b.setAttribute('loading', '');
      else b.removeAttribute('loading');
    }
  };

  const clearStatus = (): void => status.replaceChildren();

  const showProgress = (label: string): void => {
    clearStatus();
    const bar = document.createElement('caos-progress-bar');
    bar.setAttribute('indeterminate', '');
    bar.setAttribute('label', label);
    status.append(bar);
  };

  const showError = (message: string): void => {
    clearStatus();
    const banner = document.createElement('caos-notice-banner');
    banner.setAttribute('intent', 'danger');
    banner.setAttribute('title', 'Install failed');
    banner.textContent = message;
    status.append(banner);
  };

  const showSuccess = (label: string): void => {
    clearStatus();
    const banner = document.createElement('caos-notice-banner');
    banner.setAttribute('intent', 'success');
    banner.setAttribute('title', 'Installed');
    banner.textContent = `${label} installed. Bringing your workspace up to date…`;
    status.append(banner);
  };

  /**
   * CAOS-1715 — the install was accepted and installed nothing, because it was already there.
   *
   * A SEPARATE BANNER RATHER THAN A REWORDED SUCCESS, for the reason CAOS-1712 settled on the
   * removal side: the two outcomes are told apart by the title as much as the sentence, and the
   * title is what a person reads first. `intent` stays non-danger because this is not a failure —
   * the request was accepted and the workspace is in the state that was asked for. Painting it red
   * would trade one wrong claim for another.
   *
   * Phrased HERE and not in the kernel, which is the same split CAOS-1516 drew: the kernel carries
   * the fact, each surface says its own piece. The roster surfaces say "the workspace was already
   * in the state this asked for" because they serve every verb; this surface knows the verb is an
   * install and can say the more useful thing.
   */
  const showAlreadyInstalled = (label: string): void => {
    clearStatus();
    const banner = document.createElement('caos-notice-banner');
    banner.setAttribute('intent', 'info');
    banner.setAttribute('title', 'Already installed');
    banner.textContent = `${label} was already installed. Nothing changed.`;
    status.append(banner);
  };

  const close = (): void => {
    overlay.remove();
  };

  /** Install every package for an entry, in order. Progress + error + success are surfaced here. */
  const installEntry = async (entryId: string): Promise<InstallOutcome> => {
    const entry = entries.find((e) => e.id === entryId);
    if (!entry) return { ok: false, error: `Unknown catalog entry "${entryId}".` };
    // CAOS-287 — already in the workspace. Refused HERE and not only by withholding the button,
    // because `installEntry` is also the programmatic entry point on the handle: a caller driving it
    // directly would otherwise re-run an install the surface deliberately does not offer.
    // CAOS-1715 — marked as a no-op, and this is a SECOND producer of the claim rather than a
    // duplicate of the kernel's: it answers before the kernel is reached, so nothing downstream
    // could mark it. Reachable only programmatically, because the surface withholds the button for
    // an installed entry, which is precisely why it needed its own test.
    if (isInstalled(entry)) return { ok: true, noop: true };
    if (installing) return { ok: false, error: 'An install is already in progress.' };

    /*
     * CAOS-1737 — FOUND BY THE REVIEW OF #909, in the surface that ticket was not about.
     *
     * This was `list.querySelector(\`[data-entry-id="${'${entry.id}'}"] .caos-picker__entry-action\`)`
     * — a catalogue id interpolated into a selector, re-parsed on every call. An id holding a double
     * `"` character makes it malformed, so `querySelector` raises SyntaxError rather than returning null,
     * and it raises it HERE: before `setBusy`, inside an `installEntry` whose callers discard the
     * promise. A person pressing Install got no busy state, no progress line and no error.
     *
     * Identical to the defect this ticket removed from `exchange-surface.ts`, still live in the
     * first-run onboarding flow (`empty-workspace.ts` still opens this picker), and it is why the
     * source gate now covers this file too. Fixing the exchange alone would have left the claim
     * that the CLASS was closed untrue while reading as though it were.
     *
     * The picker's list is drawn ONCE, so holding the node is sound here and is what `setBusy`
     * already takes — the defect was only in how the node was found. Found by comparing the row's
     * own dataset value, so nothing is reparsed and any id works.
     */
    const button = [...list.querySelectorAll<HTMLElement>('.caos-picker__entry')]
      .find((row) => row.dataset.entryId === entry.id)
      ?.querySelector<HTMLElement>('.caos-picker__entry-action');
    setBusy(true, button ?? undefined);

    let outcome: InstallOutcome = { ok: true };
    /**
     * CAOS-1715 — AN ENTRY IS A NO-OP ONLY IF EVERY PACKAGE IN IT NO-OPPED.
     *
     * A catalog entry can ship more than one package (the workspace entry ships three), and this
     * loop kept only the LAST outcome. So an entry whose first package really installed and whose
     * last was already present reported "already installed" while the workspace had in fact
     * changed — the same false claim this ticket exists to remove, pointing the other way, and
     * reachable by anyone adding one app to a workspace that already has its siblings.
     *
     * Starts undefined rather than `true` so that an entry shipping NO packages cannot report a
     * no-op it never established: it reaches the success path, which is the pre-existing
     * behaviour, rather than claiming "already installed" about nothing.
     *
     * An earlier draft of this comment said such an entry is "refused upstream by `isInstalled`'s
     * `entry.packages.length > 0` guard". That is wrong and review caught it: the length guard
     * governs only whether `isInstalled` returns true, so an empty entry still reaches here. The
     * protection is this initialiser, not that guard.
     */
    let everyPackageNoop: boolean | undefined;
    for (const pkg of entry.packages) {
      showProgress(`Installing ${entry.label}…`);
      /**
       * CAOS-1728 — which resolver installs this package is a property of the CATALOG ENTRY, not
       * of the picker or the org. An entry that declares a universal key for this package is
       * saying "this one is not in the compiled-in registry"; every entry in STANDARD_CATALOG
       * declares none, so this reads `undefined` and the line below is the one that has always run.
       *
       * The missing-implementation case is reported rather than fallen through. A client without
       * `installByKey` cannot install this package at all, and calling `install(pkg)` instead would
       * reach the standard resolver and come back with "no installable package is registered for
       * pkg_ws_slate" — a true sentence about the wrong registry, which would send whoever reads it
       * looking for a missing package rather than a missing client method.
       */
      const universalKey = entry.universalKeys?.[pkg];
      if (universalKey === undefined) {
        outcome = await client.install(pkg);
      } else if (client.installByKey === undefined) {
        outcome = {
          ok: false,
          code: 'install.by_key_unsupported',
          error: `${entry.label} installs by universal key, which this install client cannot do.`,
        };
      } else {
        outcome = await client.installByKey(universalKey, pkg);
      }
      if (!outcome.ok) break;
      everyPackageNoop = (everyPackageNoop ?? true) && outcome.noop === true;
    }

    if (!outcome.ok) {
      showError(outcome.error ?? 'The package could not be installed.');
      setBusy(false);
      return outcome;
    }

    const nothingChanged = everyPackageNoop === true;
    outcome = { ...outcome, noop: nothingChanged };
    setBusy(false);
    if (nothingChanged) {
      /**
       * A NO-OP DOES NOT REBOOT AND DOES NOT CLOSE, and that is the half of this fix that makes
       * the rest of it visible.
       *
       * Caught in review: the banner was being appended and then `close()` ran `overlay.remove()`
       * microseconds later, so the sentence telling the two outcomes apart was never on screen
       * long enough to read. The tests still passed, because `handle.element` holds the detached
       * tree — a banner can be asserted on after it has been removed from the document, which is
       * exactly the kind of green that proves nothing about what a person sees. Painting a message
       * nobody reads is the same false claim with extra steps.
       *
       * Both omissions follow the incumbent rather than inventing anything. The CAOS-287
       * already-installed path a few lines up returns without rebooting or closing for precisely
       * this case, and CAOS-1712 settled the same shape on the removal side: the dialog stays up
       * holding the one sentence that tells the two outcomes apart, and the person closes it.
       *
       * `onInstalled` is documented as "invoked once, after a successful install, before the
       * picker closes — the reboot seam". A no-op is not an install: there is no new metadata to
       * bring the workspace up to date with, so a reboot would be work done to display a change
       * that did not happen.
       */
      showAlreadyInstalled(entry.label);
      return outcome;
    }
    showSuccess(entry.label);
    // Hand back to the caller (reboot) and close. Any reboot error is the caller's to surface;
    // the install itself succeeded.
    await Promise.resolve(deps.onInstalled());
    close();
    return outcome;
  };

  for (const entry of entries) {
    const row = el('div', 'caos-picker__entry');
    row.dataset.entryId = entry.id;
    row.dataset.featured = String(Boolean(entry.featured));

    const body = el('div', 'caos-picker__entry-body');
    body.append(
      el('p', 'caos-picker__entry-label', entry.label),
      el('p', 'caos-picker__entry-summary', entry.summary),
    );


    if (isInstalled(entry)) {
      // Listed but not actionable — the same reading Salesforce's Installed Packages gives: an
      // installed package is shown so a person can SEE it is there, not so they can install it
      // again. Deliberately not a disabled button, which reads as "this failed" rather than "this
      // is done", and deliberately not hidden, which would make a workspace look emptier than it is.
      row.dataset.installed = 'true';
      const badge = el('span', 'caos-picker__entry-installed', 'Installed');
      badge.dataset.entryId = entry.id;
      row.append(body, badge);
      list.append(row);
      continue;
    }

    /**
     * CAOS-401 — where what this entry requires will be said, once the Engine has answered.
     *
     * Created only past the installed check above, and deliberately: an installed entry is a
     * statement rather than a decision, so there is nothing to tell somebody before they commit to
     * it — they have already committed, and the requirement was met by the install that happened.
     *
     * Drawn empty and filled in asynchronously rather than awaited before the list appears. The
     * picker opens on a bare org with somebody waiting to press a button, and a screen that stalls
     * behind a round trip per package to explain itself is worse than one that explains itself a
     * moment later. It stays hidden until it has something to say, so an Engine that cannot answer
     * — an older kernel, a failed read — leaves no empty line behind.
     */
    const prereqs = el('p', 'caos-picker__entry-prereqs');
    prereqs.hidden = true;
    prereqs.dataset.entryId = entry.id;
    body.append(prereqs);
    prerequisiteSlots.set(entry.id, prereqs);

    const action = document.createElement('caos-button');
    action.className = 'caos-picker__entry-action';
    action.setAttribute('variant', entry.featured ? 'primary' : 'secondary');
    action.setAttribute('size', 'sm');
    action.setAttribute('data-entry-id', entry.id);
    action.textContent = deps.group === 'workspace' ? 'Install' : 'Deploy';
    action.addEventListener('click', () => {
      if (installing) return;
      void installEntry(entry.id);
    });
    installButtons.push(action);

    row.append(body, action);
    list.append(row);
  }

  wrap.append(list, status);

  /**
   * CAOS-401 — ask the Engine what each listed entry requires, and say so under it.
   *
   * ── WHY THE ENGINE IS ASKED AT ALL ─────────────────────────────────────────────────────────
   * The catalog knows which packages an entry ships; only the Engine knows what those packages
   * DECLARE, and only the org knows which of those declarations are already met. Reading the
   * requirement from a list kept here by hand is the failure the catalog's own header already
   * names about its install order — a rule the other ways in can walk straight past. The preview
   * resolves through the same walk the install performs, so what is shown is what will happen.
   *
   * ── WHAT THE ENTRY SUPPLIES ITSELF IS NOT A REQUIREMENT ────────────────────────────────────
   * An entry can ship several packages, and the workspace entry ships three that depend on each
   * other. "Workspace needs the component library" is true of the packages and useless to the
   * person: the button they are looking at installs it. So a prerequisite naming a package this
   * same entry ships is dropped, and what is left is what the entry needs from outside itself.
   */
  const describeRequirements = async (): Promise<void> => {
    if (typeof client.preview !== 'function') return; // a boundary that cannot answer says nothing
    await Promise.all(
      entries.map(async (entry) => {
        const slot = prerequisiteSlots.get(entry.id);
        if (!slot) return; // an installed entry has no slot: it is a statement, not a decision
        const collected: Prerequisite[] = [];
        for (const packageId of entry.packages) {
          // The shipped client turns every failure into `ok:false` and never throws, but the seam
          // is injectable and an implementation that throws would otherwise escape here as an
          // unhandled rejection — `describeRequirements` is fired and not awaited, so there is
          // nothing above to catch it. A throw means the same thing as `ok:false` does: this
          // screen cannot say what the app needs, so it says nothing.
          let outcome;
          try {
            outcome = await client.preview!(packageId);
          } catch {
            return;
          }
          if (!outcome.ok) return; // partial knowledge would read as complete; say nothing instead
          collected.push(...(outcome.prerequisites ?? []));
        }
        const external = collected.filter((p) => !entry.packages.includes(p.package));
        const sentence = prerequisiteSentence(external, catalogEntries);
        if (!sentence) return;
        slot.textContent = sentence;
        slot.hidden = false;
      }),
    );
  };
  // Deliberately not awaited: the list is already on screen and usable, and this only adds to it.
  void describeRequirements();

  // Foot — a cancel control (the scrim/Escape also close).
  const foot = el('div', 'caos-picker__foot');
  const cancel = document.createElement('caos-button');
  cancel.setAttribute('variant', 'ghost');
  cancel.setAttribute('size', 'sm');
  cancel.textContent = 'Cancel';
  cancel.addEventListener('click', () => {
    if (!installing) close();
  });
  foot.append(cancel);
  wrap.append(foot);

  overlay.append(wrap);

  // The overlay fires a cancelable close request on scrim-click / Escape; honour it unless an
  // install is mid-flight (don't drop the user out of a running install).
  overlay.addEventListener('caos-overlay-close', (e) => {
    if (installing) {
      e.preventDefault();
      return;
    }
    close();
  });

  mountInto.append(overlay);

  return { element: overlay, close, installEntry };
}
